import { useCallback, useEffect, useState } from 'react';
import {
  Box,
  TableCell,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import { AxiosError } from 'axios';
import { useNavigate } from 'react-router';

import { getAccessLogs } from '../../constants/api';
import { IApiAccessLog, IApiErrorResponse } from '../../constants/api-interfaces';
import { canAccessDashboardRoute } from '../../constants/utils';
import StyledTable from '../../components/reusable/StyledTable';
import { useAuth } from '../../contexts/AuthContext';
import { useSnackbar } from '../../contexts/SnackbarContext';

const ACCESS_LOGS_PER_PAGE = 20;

const formatTeamMember = (params: {
  employeeNumber?: string | null;
  firstName?: string | null;
  lastName?: string | null;
}) => {
  const parts = [`#${params.employeeNumber}`, params.firstName, params.lastName].filter(Boolean);

  return parts.length > 0 ? parts.join(' ') : '—';
};

const formatTimestamp = (value?: string) => {
  if (!value) {
    return '—';
  }

  const parsedDate = new Date(value);

  if (Number.isNaN(parsedDate.getTime())) {
    return value;
  }

  return parsedDate.toLocaleString();
};

export default function AccessLogs() {
  const navigate = useNavigate();
  const { auth } = useAuth();
  const { snackbarDispatch } = useSnackbar();
  const [logs, setLogs] = useState<IApiAccessLog[]>([]);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const currentRole = auth?.user?.role ?? 'teammember';
  const canAccessPage = canAccessDashboardRoute(currentRole, 'access-logs');

  useEffect(() => {
    if (!canAccessPage) {
      navigate('/dashboard', { replace: true });
    }
  }, [canAccessPage, navigate]);

  const totalPages = Math.ceil(total / ACCESS_LOGS_PER_PAGE);

  const fetchLogs = useCallback(async (nextPage: number) => {
    setIsLoading(true);

    try {
      const response = await getAccessLogs(nextPage + 1, ACCESS_LOGS_PER_PAGE);
      const parsedTotal = Number(response.data.total ?? 0);
      const safeTotal = Number.isFinite(parsedTotal) && parsedTotal > 0 ? parsedTotal : 0;

      setLogs(response.data.logs || []);
      setTotal(safeTotal);
    } catch (error) {
      const axiosError = error as AxiosError<IApiErrorResponse>;
      snackbarDispatch({
        type: 'setSnackbar',
        payload: {
          message: axiosError.response?.data?.message || 'Unable to load access logs.',
          error: true,
        },
      });
    } finally {
      setIsLoading(false);
    }
  }, [snackbarDispatch]);

  useEffect(() => {
    if (!canAccessPage) {
      return;
    }

    fetchLogs(page);
  }, [canAccessPage, fetchLogs, page]);

  useEffect(() => {
    const lastPageIndex = Math.max(totalPages - 1, 0);

    if (page > lastPageIndex) {
      setPage(lastPageIndex);
    }
  }, [page, totalPages]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    const lastPageIndex = Math.max(totalPages - 1, 0);

    if (newPage < 0 || newPage > lastPageIndex) {
      return;
    }

    setPage(newPage);
  };

  if (!canAccessPage) {
    return null;
  }

  return (
    <Box sx={{ px: { xs: 2, md: 4 }, py: 3 }}>
      <Typography variant="h4" sx={{ fontSize: '28px', fontWeight: 700, mb: 2 }}>
        Access Logs
      </Typography>

      <StyledTable
        columns={[
          { label: 'Timestamp' },
          { label: 'Action' },
          { label: 'Team member' },
          { label: 'Target Team member' },
          { label: 'Result', align: 'center' },
        ]}
        hasRows={logs.length > 0}
        isLoading={isLoading}
        loadingMessage="Loading access logs..."
        emptyMessage="No access logs found."
        pagination={
          logs.length > 0
            ? (
              <TablePagination
                component="div"
                count={total}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={20}
                rowsPerPageOptions={[ACCESS_LOGS_PER_PAGE]}
              />
            )
            : null
        }
      >
        {logs.map((log) => (
          <TableRow key={log.id} hover>
            <TableCell>{formatTimestamp(log.createdAt)}</TableCell>
            <TableCell>{log.action || '—'}</TableCell>
            <TableCell>
              {formatTeamMember({
                employeeNumber: log.employeeNumber,
                firstName: log.employeeFirstName,
                lastName: log.employeeLastName,
              })}
            </TableCell>
            <TableCell>
              {formatTeamMember({
                employeeNumber: log.targetEmployeeNumber,
                firstName: log.targetEmployeeFirstName,
                lastName: log.targetEmployeeLastName,
              })}
            </TableCell>
            <TableCell align="center">{log.success ? 'Success' : 'Failed'}</TableCell>
          </TableRow>
        ))}
      </StyledTable>
    </Box>
  );
}
